/**
 * Shared machinery for the dB and frequency rulers.
 *
 * Both draw thin lines and small text onto a canvas the *host* owns and
 * positions, which is a different situation from the main canvas and brings two
 * problems with it:
 *
 * - **The host controls the size**, and it can change without a window resize —
 *   a flex reflow, a collapsing panel, a track count change. A `resize` listener
 *   would miss all of those, so each scale observes its own element.
 * - **Everything drawn is one pixel wide.** A tick at a fractional device
 *   position is a two-pixel grey smear, and a column of them at drifting
 *   subpixel phases looks like the ruler is vibrating. Same rule as the
 *   waveform: snap to the device grid.
 *
 * Subclasses implement `render(width, height)` in CSS pixels and use the
 * helpers here to place things.
 */

export class ScaleCanvas {
  /**
   * @param {HTMLCanvasElement} canvas - owned and positioned by the host
   * @param {Object} [options]
   * @param {Object} [options.theme] - resolved scale theme block
   * @param {"left"|"right"} [options.tickAlign] - overrides `theme.tickAlign`
   * @param {number} [options.padding] - overrides `theme.padding`
   */
  constructor(canvas, options = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.options = options;
    this.theme = options.theme || {};

    this.observer = null;
    if (typeof ResizeObserver !== "undefined") {
      this.observer = new ResizeObserver(() => this.draw());
      this.observer.observe(canvas);
    }
  }

  /** @param {Object} theme - resolved scale theme block */
  setTheme(theme) {
    this.theme = theme || {};
    this.draw();
  }

  /**
   * The backing store scale actually in effect.
   *
   * Read off the canvas rather than `window.devicePixelRatio` for the same
   * reason the main renderer does: the two disagree between a display change and
   * the resize that reacts to it, and it's the backing store that decides where
   * a device pixel is.
   */
  get pixelRatio() {
    const cssWidth = this.canvas.offsetWidth;
    if (!cssWidth) return window.devicePixelRatio || 1;
    return this.canvas.width / cssWidth;
  }

  /** Match the backing store to the CSS box. Returns false if it has no size yet. */
  resize() {
    const dpr = window.devicePixelRatio || 1;
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight;
    if (!width || !height) return false;

    const wanted = { w: Math.round(width * dpr), h: Math.round(height * dpr) };
    if (this.canvas.width !== wanted.w || this.canvas.height !== wanted.h) {
      // Assigning either dimension resets the transform, so the scale has to be
      // re-applied after — not once at construction.
      this.canvas.width = wanted.w;
      this.canvas.height = wanted.h;
    }

    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.scale(dpr, dpr);
    return true;
  }

  draw() {
    if (!this.resize()) return;

    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight;

    this.ctx.clearRect(0, 0, width, height);
    if (this.theme.background && this.theme.background !== "transparent") {
      this.ctx.fillStyle = this.theme.background;
      this.ctx.fillRect(0, 0, width, height);
    }

    this.render(width, height);
  }

  /** @abstract @param {number} width @param {number} height - CSS pixels */
  render() {}

  /**
   * A horizontal rule exactly one device pixel thick, on the device grid.
   *
   * `fillRect` rather than a stroked path: a stroke straddles its coordinate, so
   * it needs a half-pixel nudge that changes with the ratio. A filled rect
   * snapped to the grid is unambiguous at any ratio.
   */
  hairline(x0, x1, y, color) {
    const dpr = this.pixelRatio;
    const snapped = Math.round(y * dpr) / dpr;

    this.ctx.fillStyle = color;
    this.ctx.fillRect(Math.min(x0, x1), snapped, Math.abs(x1 - x0), 1 / dpr);
  }

  /**
   * Which edge of this canvas the ticks sit against.
   *
   * A ruler placed to the *left* of the waveform wants "right" so its ticks run
   * up against the lane; one on the right wants "left". Per-instance first, then
   * the theme — a host laying out one on each side needs to say so per ruler,
   * but a host that wants one convention everywhere should only say it once.
   */
  get tickAlign() {
    return this.options.tickAlign || this.theme.tickAlign || "left";
  }

  /** Inset from the aligned edge. 0 puts the ticks flush against it. */
  get padding() {
    return this.options.padding ?? this.theme.padding ?? 0;
  }

  /** Where a tick of `length` starts and ends. */
  tickSpan(width, length) {
    const pad = this.padding;
    return this.tickAlign === "left"
      ? [pad, pad + length]
      : [width - pad - length, width - pad];
  }

  /** Where a label is anchored, and how to align it — always outside the ticks. */
  labelAnchor(width, length, gap) {
    const pad = this.padding;
    return this.tickAlign === "left"
      ? { x: pad + length + gap, align: "left" }
      : { x: width - pad - length - gap, align: "right" };
  }

  /** Room a ruler needs: the inset, its longest tick, the gap, and the label. */
  widthFor(widestLabel, tickLength, gap) {
    return Math.ceil(this.padding + tickLength + gap + widestLabel + 2);
  }

  /**
   * Drop entries that would overlap, keeping the most important.
   *
   * Ticks arrive in priority order and are placed only if they clear everything
   * already placed. That's what makes a short scale degrade to "the ones that
   * matter" rather than to unreadable overlapping text — and why the caller
   * orders the list by importance rather than by position.
   *
   * @param {Array<{y: number}>} candidates - most important first
   * @param {number} minimumGap - centre-to-centre, in CSS pixels
   */
  static cull(candidates, minimumGap) {
    const kept = [];

    for (const candidate of candidates) {
      if (kept.every((placed) => Math.abs(placed.y - candidate.y) >= minimumGap)) {
        kept.push(candidate);
      }
    }

    return kept;
  }

  destroy() {
    this.observer?.disconnect();
    this.observer = null;
    this.canvas = null;
    this.ctx = null;
  }
}
